import axios from "axios";
import {
    addMissingOrder,
    AdminStat,
    AdminStatSchema,
    draftOrder,
    draftOrderSchema,
    IAiTitlesAndPlans,
    INetlinkingOrderInfo,
    ISuggestedWords,
    ITotalOrderWithoutCategories,
    ManagerStat,
    ManagerStatSchema,
    Order,
    TOffSiteOrderType,
    TOnSiteOrderType,
    TOrderType,
    TWritingType,
    UpsertDraftOrdersDTO,
    UpsertPublishOn,
    WritingType,
} from "../types/orders/order";
import { OrderSchema, UpsertOrderDTO } from "../types/orders/order";
import { toCamelCase, toSnakeCase } from "../utils/objectHelper";
import qs from "qs";
import { SimpleFormSelectOption } from "../components/form/SimpleFormSelect";
import { TContent } from "../types/Site";
import { getKeywordFromString } from "../utils/keywordsHelper";
import { CreateOrders, TFrequency } from "../types/CreateOrders";
import { UpsertBulkRankingOrder } from "../types/BulkRankingOrder";
import { TLangType } from "../types/LangType";
import { StockListOrderSchema } from "../types/orders/stockOrderPage";
import { OnlineOrderSchema } from "../types/orders/onlineOrderPage";
import moment from "moment-timezone";
import { onlineZoomContentSchema } from "../types/orders/onlineZoomContentPage";
import { OrderOnSiteGrid, OrderOnSiteGridSchema } from "../types/orders/onSite";
import { OrderOffSiteGrid, OrderOffSiteGridSchema } from "../types/orders/offSite";
import { IData } from "../containers/BulkNetlinkingModal/BulkNetlinkingUtils";
import { IData as IDataSilo } from "../containers/BulkSiloModal/BulkSiloUtils";
import { IVictoryKeyword } from "../types/keywords";
import { toProcessOrder, toProcessOrderSchema } from "../types/orders/toProcessOrderPage";
import { ProofreadOrderPage, ProofreadOrderPageSchema } from "../types/orders/proofreadOrderPage";
export interface IDaysOffset {
    lastDays?: number;
    nextDays?: number;
}

export const OrderTransport = {
    createBulkBoostNetlinkingOrders: async (data: IData[]) => {
        const response = await axios.post(`/orders/createBulkBoostNetlinking`, {
            data,
        });

        return response.data.map((current: any) => {
            return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    createBulkBoostSiloOrders: async (data: IDataSilo[]) => {
        const response = await axios.post(`/orders/createBulkBoostSilo`, {
            data,
        });

        return response.data.map((current: any) => {
            return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    createOrderForVictoryKeywords: async ({
        moneySiteId,
        victoryKeywords,
        frequency,
        writingType,
    }: {
        moneySiteId: number;
        victoryKeywords: IVictoryKeyword[];
        frequency: TFrequency;
        writingType: TWritingType;
    }): Promise<string[]> => {
        const { data } = await axios.post(`/orders/createOrderForVictoryKeywords`, {
            moneySiteId,
            victoryKeywords,
            frequency,
            writingType,
        });
        return data.map((current: any) =>
            OrderSchema.cast(toCamelCase(current), { stripUnknown: true })
        );
    },

    addAiManuelToContent: async (
        contentId: number,
        aiManuelContent: {
            text: string;
            title: string;
            metaTitle: string;
            wordCount: number;
            metaDescription: string;
        }
    ): Promise<any> => {
        const { data } = await axios.put(`/addAiManuelToContent/${contentId}`, {
            ...toSnakeCase(aiManuelContent),
        });
        return toCamelCase(data);
    },

    findKeywordsVictory: async (moneySiteId: number): Promise<IVictoryKeyword[]> => {
        const { data } = await axios.get(`/order/findKeywordsVictory/${moneySiteId}`, {});
        return data;
    },

    findAllUrlsByMoneySiteOptions: async (
        moneySiteId: number
    ): Promise<Array<SimpleFormSelectOption & { siteOrderType: TOffSiteOrderType }>> => {
        const { data } = await axios.get(`/order/findAllUrlsByMoneySiteOptions/${moneySiteId}`, {});
        return data;
    },

    checkIfKeywordsAreBoosted: async (
        keywordToCheck: Array<{ orderId: number; keyword: string }>
    ): Promise<Array<{ orderId: number; keyword: string }>> => {
        const { data } = await axios.post(`/orders/checkIfKeywordsAreBoosted`, {
            keywordToCheck,
        });
        return data;
    },

    listByMoneySiteOffSite: async (
        moneySiteId: number,
        orderType: TOffSiteOrderType
    ): Promise<Array<OrderOffSiteGrid>> => {
        const { data } = await axios.get(
            `/orders/listByMoneySiteOffSite?moneySiteId=${moneySiteId}&orderType=${orderType}`,
            {}
        );
        return data.map((current: any) => {
            return OrderOffSiteGridSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    listByMoneySiteOnSite: async (
        moneySiteId: number,
        orderType: TOnSiteOrderType
    ): Promise<Array<OrderOnSiteGrid>> => {
        const { data } = await axios.get(
            `/orders/listByMoneySiteOnSite?moneySiteId=${moneySiteId}&orderType=${orderType}`,
            {}
        );
        return data.map((current: any) => {
            return OrderOnSiteGridSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    listHistoryByMoneySiteOffSite: async (
        moneySiteId: number
    ): Promise<Array<OrderOffSiteGrid>> => {
        const { data } = await axios.get(`/histories?moneySiteId=${moneySiteId}`, {});
        return data;
    },

    listHistoryByMoneySiteOnSite: async (moneySiteId: number): Promise<Array<OrderOffSiteGrid>> => {
        const { data } = await axios.get(`/histories?moneySiteId=${moneySiteId}`, {});
        return data;
    },

    createCampaigns: async (id: number, { maxCost, cpm }: { maxCost: number; cpm: number }) => {
        const { data } = await axios.post(`/createCampaign/${id}`, {
            maxCost,
            cpm,
        });
        return data;
    },
    createPopCashCampaigns: async (id: number, popPrice: number) => {
        const { data } = await axios.post(`/createPopCashCampaign/${id}`, {
            popPrice,
        });
        return data;
    },
    addWriters: async (orderIds: Array<number>, writerId: number) => {
        const { data } = await axios.post(`/order/addWriter`, {
            orderIds,
            writerId,
        });
        return data;
    },

    addProofreader: async (orderIds: Array<number>, proofreaderId: number) => {
        const { data } = await axios.post(`/order/addProofreader`, {
            orderIds,
            proofreaderId,
        });
        return data;
    },
    getOrderById: async (orderId: number) => {
        const { data } = await axios.get(`/order/${orderId}`, {});
        return OrderSchema.cast(toCamelCase(data), { stripUnknown: true });
    },

    getOrdersById: async (ordersId: number[]): Promise<Array<Order>> => {
        const { data } = await axios.get(`/orders/byOrdersId?ordersId=${ordersId.toString()}`, {});
        return data.map((current: any) =>
            OrderSchema.cast(toCamelCase(current), { stripUnknown: true })
        );
    },

    minimumCpmPerLang: async (lang: TLangType) => {
        const { data } = await axios.get(`/order/getMinimumCpmPerLang?lang=${lang}`, {});
        return data;
    },

    createBoostSiloOrders: async (id: number, keywords: string[]) => {
        const { data } = await axios.post(`/order/createBoostSilos/${id}`, {
            data: keywords,
        });

        return data.map((current: any) => {
            return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    createBoostNetlinkingOrders: async (
        id: number,
        netlinkingOrderInfo: INetlinkingOrderInfo[]
    ) => {
        const { data } = await axios.post(`/orders/createBoostNetlinking/${id}`, {
            netlinkingOrderInfo: toSnakeCase(netlinkingOrderInfo),
        });

        return data.map((current: any) => {
            return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    titleIAList: async () => {
        const { data } = await axios.get(`/orders/titleIA/list`, {});
        return data.map((current: any) => {
            return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },
    imageIAList: async () => {
        const { data } = await axios.get(`/orders/imageIA/list`, {});
        return data.map((current: any) => {
            return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },
    withModifyImage: async () => {
        const { data } = await axios.get(`/orders/review/modifyImage/list`, {});
        return data.map((current: any) => {
            return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },
    list: async () => {
        const { data } = await axios.get(`/orders`, {});
        return data.map((current: any) => {
            return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    findAllUsedRDIdByMoneySiteID: async (moneySiteID: number): Promise<Array<Number>> => {
        const { data } = await axios.get(
            `/orders/findAllUsedRDIdByMoneySiteID?moneySiteID=${moneySiteID}`,
            {}
        );
        return data;
    },
    listLastDays: async (lastDays = 7) => {
        const { data } = await axios.get(`/orders/lastDays?lastDays=${lastDays}`, {});
        return data.map((current: any) => {
            return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    listLibrary: async () => {
        const { data } = await axios.get(`/orders/listLibrary`, {});
        return data.map((current: any) => {
            return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    listOnGoing: async () => {
        const { data } = await axios.get(`/orders/onGoing`, {});
        return data.map((current: any) => {
            return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },
    boost: {
        list: async (params?: any) => {
            const { data } = await axios.get(
                `/orders/boost/list${params ? `?${qs.stringify(params)}` : ""}`,
                {}
            );
            return data;
        },
    },
    addBoostToOrders: async (keywordToChange: Array<{ orderId: number; keyword: string }>) => {
        await axios.request({
            method: "put",
            url: `/order/addBoostToOrders`,
            data: { keywordToChange },
        });
    },

    addBoostToFavorite: async (boostMetricsIds: number[]) => {
        await axios.request({
            method: "put",
            url: `/order/addBoostToFavorite`,
            data: { boostMetricsIds },
        });
    },
    stock: {
        list: async (params?: any) => {
            const { data } = await axios.get(
                `/orders/stock/list${params ? `?${qs.stringify(params)}` : ""}`,
                {}
            );

            return data.map((current: any) => {
                return StockListOrderSchema.cast(toCamelCase(current), { stripUnknown: true });
            });
        },
    },

    write: {
        list: async (params?: any) => {
            const { data } = await axios.get(
                `/orders/toWrite/list${params ? `?${qs.stringify(params)}` : ""}`,
                {}
            );
            return data.map((current: any) => {
                return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
            });
        },
        export: async (params?: any) => {
            const response = await axios.get(
                `/orders/write/export${params ? `?${qs.stringify(params)}` : ""}`,
                {}
            );

            return response.data;
        },
        columnsValues: async (params?: any) => {
            const { data } = await axios.get(
                `/orders/toWrite/columnsValues${params ? `?${qs.stringify(params)}` : ""}`,
                {}
            );
            return data[0];
        },
        findTotalFiltered: async (params?: any): Promise<number> => {
            const { data } = await axios.get(
                `/orders/toWrite/findTotalFiltered${params ? `?${qs.stringify(params)}` : ""}`,
                {}
            );

            return data;
        },
        findTotal: async (): Promise<number> => {
            const { data } = await axios.get<number>(`/orders/toWrite/findTotal`);
            return data;
        },
    },

    reviewed: {
        blog: {
            list: async (params?: any) => {
                const { data } = await axios.get(
                    `/orders/reviewed/blog/list${params ? `?${qs.stringify(params)}` : ""}`,
                    {}
                );
                return data.map((current: any) => {
                    return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
                });
            },
            export: async (params?: any) => {
                const response = await axios.get(
                    `/orders/reviewed/blog/export${params ? `?${qs.stringify(params)}` : ""}`,
                    {}
                );

                return response.data;
            },
            columnsValues: async (params?: any) => {
                const { data } = await axios.get(
                    `/orders/reviewed/blog/columnsValues${
                        params ? `?${qs.stringify(params)}` : ""
                    }`,
                    {}
                );
                return data[0];
            },
            findTotalFiltered: async (params?: any): Promise<number> => {
                const { data } = await axios.get(
                    `/orders/reviewed/blog/findTotalFiltered${
                        params ? `?${qs.stringify(params)}` : ""
                    }`,
                    {}
                );

                return data;
            },
            findTotal: async (): Promise<number> => {
                const { data } = await axios.get<number>(`/orders/reviewed/blog/findTotal`);
                return data;
            },
        },
        lp: {
            list: async (params?: any) => {
                const { data } = await axios.get(
                    `/orders/reviewed/lp/list${params ? `?${qs.stringify(params)}` : ""}`,
                    {}
                );
                return data.map((current: any) => {
                    return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
                });
            },
            export: async (params?: any) => {
                const response = await axios.get(
                    `/orders/reviewed/lp/export${params ? `?${qs.stringify(params)}` : ""}`,
                    {}
                );

                return response.data;
            },
            columnsValues: async (params?: any) => {
                const { data } = await axios.get(
                    `/orders/reviewed/lp/columnsValues${params ? `?${qs.stringify(params)}` : ""}`,
                    {}
                );
                return data[0];
            },
            findTotalFiltered: async (params?: any): Promise<number> => {
                const { data } = await axios.get(
                    `/orders/reviewed/lp/findTotalFiltered${
                        params ? `?${qs.stringify(params)}` : ""
                    }`,
                    {}
                );

                return data;
            },
            findTotal: async (): Promise<number> => {
                const { data } = await axios.get<number>(`/orders/reviewed/lp/findTotal`);
                return data;
            },
        },
    },

    listDelivered: async (params?: any) => {
        const { data } = await axios.get(
            `/orders/listDelivered${params ? `?${qs.stringify(params)}` : ""}`,
            {}
        );
        return data.map((current: any) => {
            return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },
    findDeliveredTotalFiltered: async (params?: any): Promise<number> => {
        const { data } = await axios.get(
            `/orders/findDeliveredTotalFiltered${params ? `?${qs.stringify(params)}` : ""}`,
            {}
        );
        return data;
    },
    findTotalDelivered: async (): Promise<number> => {
        const { data } = await axios.get<number>(`/orders/findTotalDelivered`);
        return data;
    },

    findAiTitleAndPlanTotal: async (): Promise<Array<IAiTitlesAndPlans>> => {
        const { data } = await axios.get<Array<IAiTitlesAndPlans>>(`/orders/findAiTitleAndPlan`);
        return data;
    },

    listToProofreader: async (): Promise<ProofreadOrderPage[]> => {
        const { data } = await axios.get("/orders/listToProofreader", {});

        return data.map((current: any) => {
            return ProofreadOrderPageSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    listToProcess: async (): Promise<Array<toProcessOrder>> => {
        const { data } = await axios.get(`/orders/listToProcess`, {});

        return data.map((current: any) => {
            return toProcessOrderSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    publish: {
        blog: {
            list: async (params?: any) => {
                const { data } = await axios.get(
                    `/orders/publish/blog/list${params ? `?${qs.stringify(params)}` : ""}`,
                    {}
                );
                return data;
            },
        },
        lp: {
            list: async (params?: any) => {
                const { data } = await axios.get(
                    `/orders/publish/lp/list${params ? `?${qs.stringify(params)}` : ""}`,
                    {}
                );
                return data;
            },
        },
    },

    listWaiting: async () => {
        const { data } = await axios.get(`/orders/listWaiting`, {});
        return data.map((current: any) => {
            return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    online: {
        all: {
            list: async (params?: any) => {
                const { data } = await axios.get(
                    `/orders/online/all/list${params ? `?${qs.stringify(params)}` : ""}`,
                    {}
                );
                return data.map((current: any) => {
                    return OnlineOrderSchema.cast(toCamelCase(current), { stripUnknown: true });
                });
            },
            export: async (params?: any) => {
                const response = await axios.get(
                    `/orders/online/all/export${params ? `?${qs.stringify(params)}` : ""}`,
                    {}
                );

                return response.data;
            },
            columnsValues: async (params?: any) => {
                const { data } = await axios.get(
                    `/orders/online/all/columnsValues${params ? `?${qs.stringify(params)}` : ""}`,
                    {}
                );
                return data[0];
            },
            findTotalFiltered: async (params?: any): Promise<number> => {
                const { data } = await axios.get(
                    `/orders/online/all/findTotalFiltered${
                        params ? `?${qs.stringify(params)}` : ""
                    }`,
                    {}
                );

                return data;
            },
            findTotal: async (): Promise<number> => {
                const { data } = await axios.get<number>(`/orders/online/all/findTotal`);
                return data;
            },
        },
        sell: {
            list: async (params?: any) => {
                const { data } = await axios.get(
                    `/orders/online/sell/list${params ? `?${qs.stringify(params)}` : ""}`,
                    {}
                );
                return data.map((current: any) => {
                    return OnlineOrderSchema.cast(toCamelCase(current), { stripUnknown: true });
                });
            },
            export: async (params?: any) => {
                const response = await axios.get(
                    `/orders/online/sell/export${params ? `?${qs.stringify(params)}` : ""}`,
                    {}
                );

                return response.data;
            },
            columnsValues: async (params?: any) => {
                const { data } = await axios.get(
                    `/orders/online/sell/columnsValues${params ? `?${qs.stringify(params)}` : ""}`,
                    {}
                );
                return data[0];
            },
            findTotalFiltered: async (params?: any): Promise<number> => {
                const { data } = await axios.get(
                    `/orders/online/sell/findTotalFiltered${
                        params ? `?${qs.stringify(params)}` : ""
                    }`,
                    {}
                );

                return data;
            },
            findTotal: async (): Promise<number> => {
                const { data } = await axios.get<number>(`/orders/online/sell/findTotal`);
                return data;
            },
        },
        network: {
            list: async (params?: any) => {
                const { data } = await axios.get(
                    `/orders/online/network/list${params ? `?${qs.stringify(params)}` : ""}`,
                    {}
                );
                return data.map((current: any) => {
                    return OnlineOrderSchema.cast(toCamelCase(current), { stripUnknown: true });
                });
            },
            export: async (params?: any) => {
                const response = await axios.get(
                    `/orders/online/network/export${params ? `?${qs.stringify(params)}` : ""}`,
                    {}
                );

                return response.data;
            },
            columnsValues: async (params?: any) => {
                const { data } = await axios.get(
                    `/orders/online/network/columnsValues${
                        params ? `?${qs.stringify(params)}` : ""
                    }`,
                    {}
                );
                return data[0];
            },
            findTotalFiltered: async (params?: any): Promise<number> => {
                const { data } = await axios.get(
                    `/orders/online/network/findTotalFiltered${
                        params ? `?${qs.stringify(params)}` : ""
                    }`,
                    {}
                );

                return data;
            },
            findTotal: async (): Promise<number> => {
                const { data } = await axios.get<number>(`/orders/online/network/findTotal`);
                return data;
            },
        },
    },

    listLanding: async (moneySiteId: number) => {
        const { data } = await axios.get(`/orders/listLanding?moneySiteId=${moneySiteId}`, {});
        return data.map((current: any) => {
            return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    listByMoneySite: async (moneySiteId: number) => {
        const { data } = await axios.get(`/orders/listByMoneySite?moneySiteId=${moneySiteId}`, {});

        return data.map((current: any) => {
            return onlineZoomContentSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    listBlog: async (moneySiteId: number) => {
        const { data } = await axios.get(`/orders/listBlog?moneySiteId=${moneySiteId}`, {});
        return data.map((current: any) => {
            return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    listNetlinking: async (moneySiteId: number) => {
        const { data } = await axios.get(`/orders/listNetlinking?moneySiteId=${moneySiteId}`, {});
        return data.map((current: any) => {
            return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    listIndexedByWritingType: async (moneySiteId: number) => {
        const { data } = await axios.get(`/indexedByWritingType/${moneySiteId}`, {});
        return data;
    },

    listTypes: async () => {
        const { data } = await axios.get(`/orders/types`, {});
        return data;
    },

    listDraft: async (id?: number): Promise<Array<Order>> => {
        const { data } = await axios.get(`/orders/draft?managerId=${id}`, {});
        return data.map((current: any) => {
            return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    listUrlByLandingAndHomePageOptions: async (
        moneySiteId: number
    ): Promise<Array<SimpleFormSelectOption & { contentType: string }>> => {
        const { data } = await axios.get<Array<SimpleFormSelectOption & { contentType: string }>>(
            `/orders/urlByLandingAndHomePageOptions?moneySiteId=${moneySiteId}`,
            {}
        );
        return data;
    },

    listApprove: async () => {
        const { data } = await axios.get(`/orders/listApprove`, {});
        return data.map((current: any) => {
            return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    toggleActive: async ({ id }: { id: number }): Promise<void> => {
        await axios.patch(`/orders/${id}/actions/toggleActive`);
    },

    removeRD: async (id: number, reason: string): Promise<Order> => {
        const { data } = await axios.patch(`/orders/${id}/actions/removeRD/${reason}`);
        return OrderSchema.cast(toCamelCase(data));
    },

    indexMe: async (id: number): Promise<void> => {
        await axios.patch(`/orders/${id}/actions/indexMe`);
    },
    findTotalOrderWithoutCategories: async (): Promise<Array<ITotalOrderWithoutCategories>> => {
        const { data } = await axios.get(`/orders/totalWithoutCategories`);
        return data;
    },

    getSuggestedWords: async (orderId: number): Promise<ISuggestedWords> => {
        const { data } = await axios.get<ISuggestedWords>(`/orders/suggestedWords/${orderId}`, {});
        return data;
    },

    getOrdersStat: async (content: TContent): Promise<Array<ManagerStat>> => {
        const { data } = await axios.get<Array<any>>(`/order/getOrdersStat?content=${content}`);
        return data.map((current) => ManagerStatSchema.cast(current, { stripUnknown: true }));
    },

    getOrdersStatForAdmin: async (content: TContent): Promise<Array<AdminStat>> => {
        const { data } = await axios.get<Array<any>>(`/order/getOrdersStat?content=${content}`);
        return data.map((current) => AdminStatSchema.cast(current, { stripUnknown: true }));
    },
    publishOn: async (orders: Array<UpsertPublishOn>): Promise<Array<Order>> => {
        const formatedData = {
            orders: {
                ...orders,
            },
        };
        const { data } = await axios.request<Array<Order>>({
            method: "put",
            url: `/orders/publishOn`,
            data: toSnakeCase(formatedData),
        });

        return data.map((order) => OrderSchema.cast(toCamelCase(order), { stripUnknown: true }));
    },

    saveDraft: async (orderDraft: UpsertDraftOrdersDTO, managerId: number): Promise<draftOrder> => {
        const data = {
            orders: orderDraft.data.map((order) => ({
                ...order,
                managerId: order.managerId ? order.managerId : managerId,
            })),
        };
        const response = await axios.request({
            method: "post",
            url: `/orders/draft`,
            data: toSnakeCase(data),
        });
        return draftOrderSchema.cast(toCamelCase(response.data), { stripUnknown: true });
    },

    save: async (order: UpsertOrderDTO, id: number | undefined): Promise<Order> => {
        const response = await axios.request({
            method: "put",
            url: `/order/${id}`,
            data: toSnakeCase(order),
        });
        return OrderSchema.cast(toCamelCase(response.data), { stripUnknown: true });
    },

    addMissing: async (order: addMissingOrder): Promise<Order> => {
        const response = await axios.request({
            method: "POST",
            url: `/order/addMissing`,
            data: toSnakeCase(order),
        });
        return OrderSchema.cast(toCamelCase(response.data), { stripUnknown: true });
    },

    silo: async (order: UpsertOrderDTO): Promise<Array<Order>> => {
        const { data } = await axios.request<Array<Order>>({
            method: "post",
            url: `/order/${order.id}/silo`,
            data: toSnakeCase(order),
        });
        return data.map((current) =>
            OrderSchema.cast(toCamelCase(current), { stripUnknown: true })
        );
    },
    addRD: async (order: UpsertOrderDTO, id: number | undefined): Promise<Order> => {
        const response = await axios.request({
            method: "put",
            url: `/order/addRD/${id}`,
            data: toSnakeCase(order),
        });
        return OrderSchema.cast(toCamelCase(response.data), { stripUnknown: true });
    },
    addUrl: async (order: UpsertOrderDTO, id: number | undefined): Promise<Order> => {
        const response = await axios.request({
            method: "put",
            url: `/order/addUrl/${id}`,
            data: toSnakeCase(order),
        });
        return OrderSchema.cast(toCamelCase(response.data), { stripUnknown: true });
    },

    saveBulk: async (orders: Array<UpsertOrderDTO> | any): Promise<Array<Order>> => {
        const formattedData = {
            orders: {
                ...orders,
            },
        };
        const { data } = await axios.request({
            method: "put",
            url: `/orders`,
            data: toSnakeCase(formattedData),
        });
        return data.map((current: any) => {
            return OrderSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    createOrders: async (data: CreateOrders, orderType: TOrderType) => {
        const response = await axios.request({
            method: "post",
            url: "/orders/create",
            data: {
                ...data,
                orderType,
                keywords: getKeywordFromString(data.keywords),
            },
        });
        return response.data;
    },

    saveBulkRanking: async (data: UpsertBulkRankingOrder) => {
        const response = await axios.request({
            method: "post",
            url: "/bulkRankingOrders",
            data: {
                ...data,
                keywords: getKeywordFromString(data.keywords),
            },
        });
        return response.data;
    },

    numberOfOrderWithOutCategoriesByManager: async (): Promise<number> => {
        const { data } = await axios.request<number>({
            method: "get",
            url: "/orders/numberOfOrderWithOutCategoriesByManager",
        });

        return data;
    },

    numberOfOrderWithOutRDLinkers: async (): Promise<number> => {
        const { data } = await axios.request<number>({
            method: "get",
            url: "/orders/numberOfOrderWithOutRDLinkers",
        });
        return data;
    },

    aiManualGuidePrompts: async (
        orderId: number,
        guidePrompts: "INTENT_SEARCH" | "TITLE" | "PLAN" | "META" | "TEXT" | "IMAGE",
        intentSearch?: string,
        title?: string,
        plan?: string,
        text?: string | undefined
    ): Promise<string> => {
        const { data } = await axios.request({
            method: "POST",
            url: `/jarvis/getAiManualPrompts/${orderId}?guidePrompts=${guidePrompts}`,
            data: {
                intentSearch,
                title,
                plan,
                text,
            },
        });

        return data;
    },

    getAiManualIntentSearch: async (orderId: number): Promise<string> => {
        const { data } = await axios.request({
            method: "GET",
            url: `/jarvis/aiManualIntentSearch/${orderId}`,
            data: {},
        });
        return data;
    },

    getAiManualTitle: async (
        orderId: number,
        intentSearch: string,
        extraPlanInformation: string
    ): Promise<string> => {
        const { data } = await axios.request({
            method: "POST",
            url: `/jarvis/aiManualTitle/${orderId}`,
            data: {
                intentSearch,
                extraPlanInformation,
            },
        });

        return data;
    },

    getAiManualPlan: async (
        orderId: number,
        title: string,
        intentSearch: string
    ): Promise<string> => {
        const { data } = await axios.request({
            method: "POST",
            url: `/jarvis/aiManualPlan/${orderId}`,
            data: {
                title,
                intentSearch,
            },
        });

        return data;
    },

    getAiManualMeta: async (orderId: number, title: string, plan: string): Promise<string> => {
        const { data } = await axios.request({
            method: "POST",
            url: `/jarvis/aiManualMeta/${orderId}`,
            data: {
                title,
                plan,
            },
        });
        return data;
    },

    getAiManualText: async (orderId: number, title: string, plan: string): Promise<string> => {
        const { data } = await axios.request({
            method: "POST",
            url: `/jarvis/aiManualText/${orderId}`,
            data: {
                title,
                plan,
            },
        });
        return data;
    },

    getAiManualPeopleAlsoAsk: async (orderId: number, peopleAlsoAsk: string): Promise<string> => {
        const { data } = await axios.request({
            method: "POST",
            url: `/jarvis/aiManualPeopleAlsoAsk/${orderId}`,
            data: {
                peopleAlsoAsk,
            },
        });
        return data;
    },

    getAiManualImage: async (orderId: number, title: string): Promise<string> => {
        const { data } = await axios.request({
            method: "POST",
            url: `/jarvis/aiManualSerp/${orderId}`,
            data: {
                title,
            },
        });
        return data;
    },

    getAiAnchor: async (
        keyword: string,
        numberOfKeywords?: number,
        lang?: TLangType
    ): Promise<{ content: string[]; price: string }> => {
        const { data } = await axios.request<{ content: string[]; price: string }>({
            method: "GET",
            url: `/jarvis/getAnchor?keyword=${keyword}&number=${numberOfKeywords}&lang=${lang}`,
        });

        return data;
    },

    findIfUrlIsAlreadyBoosted: async (url: string): Promise<string[]> => {
        const { data } = await axios.request<string[]>({
            method: "GET",
            url: `/findIfUrlIsAlreadyBoosted?url=${url}`,
        });

        return data;
    },

    getNetlinkingInformation: async (keywordInfo: {
        keyword: string;
        semRushKeywords: string[];
        numberOfKeywords: number;
        frequency: TFrequency;
        moneySiteId: number;
        lang?: TLangType;
    }): Promise<{
        anchor: string[];
        price: string;
        netlinkingOrderInfo: INetlinkingOrderInfo[];
    }> => {
        const { data } = await axios.request<{
            anchor: string[];
            price: string;
            netlinkingOrderInfo: INetlinkingOrderInfo[];
        }>({
            method: "POST",
            url: `/orders/netlinkingInformation/${keywordInfo.moneySiteId}`,
            data: JSON.stringify({
                ...keywordInfo,
            }),
        });

        return {
            ...data,
            netlinkingOrderInfo: data.netlinkingOrderInfo.map((el) => ({
                ...el,
                date: moment(el.date).tz("Europe/Paris").format("YYYY-MM-DD"),
            })),
        };
    },

    formTransformer: (object: Order): UpsertOrderDTO => {
        return {
            id: object.id,
            managerId: object.manager?.id,
            writerId: object.writer?.id,
            proofreaderId: object.proofreader?.id,
            contentId: object.content?.id,
            thematicId: object.thematic?.id,
            commentForWriter: object.commentForWriter,
            commentForLinker: object.commentForLinker,
            rate: object.rate,
            wordCount: object.wordCount,
            returnCount: object.returnCount,
            moneySiteId: object.moneySite?.id,
            platformId: object.platform?.id,
            rdId: object.rd?.id,
            contentType: object.contentType,
            links: object.links,
            keywords: object.keywords,
            orderType: object.orderType,
            url: object.url,
            approveNeeded: object.approveNeeded,
            isApproved: object.isApproved,
            priority: object.priority,
            title: object.content?.title,
            writingType: object.content?.writingType || WritingType.WRITE,
            orderPrice: object.orderPrice,
            writerPrice: object.writerPrice,
            proofreaderPrice: object.proofreaderPrice,
            publishedAt: object.publishedAt,
            createdAt: object.createdAt,
            updatedAt: object.updatedAt,
            deliveredAt: object.deliveredAt,
            reviewedAt: object.reviewedAt,
            processedAt: object.processedAt,
            sentAt: object.sentAt,
            opti: object.opti,
            writerNeeded: object.writerNeeded,
            reviewNeeded: object.reviewNeeded,
        };
    },

    formDraftTransformer: (data: Array<Order>): UpsertDraftOrdersDTO | { data: Array<any> } => {
        if (!data || data.length == 0) {
            return {
                data: [
                    {
                        id: undefined,
                        managerId: undefined,
                        thematicId: undefined,
                        commentForWriter: undefined,
                        commentForLinker: undefined,
                        wordCount: undefined,
                        moneySiteId: undefined,
                        rdId: undefined,
                        priority: false,
                        contentType: undefined,
                        writingType: undefined,
                        links: undefined,
                        platformId: undefined,
                        keywords: undefined,
                        orderPrice: undefined,
                        writerPrice: undefined,
                        orderType: undefined,
                        opti: undefined,
                        title: undefined,
                        writerNeeded: undefined,
                        reviewNeeded: undefined,
                        approveNeeded: undefined,
                        publishedAt: undefined,
                        draft: undefined,
                    },
                ],
            };
        }
        const transformedData = data.map((order) => ({
            id: order.id,
            managerId: order.manager?.id,
            thematicId: order.thematic.id,
            commentForWriter: order.commentForWriter,
            commentForLinker: order.commentForLinker,
            wordCount: order.wordCount,
            moneySiteId: order.moneySite?.id,
            platformId: order.platform?.id,
            rdId: order.rd?.id,
            priority: order.priority,
            contentType: order.contentType,
            writingType: order.content?.writingType,
            links: order.links,
            keywords: order.keywords,
            orderPrice: order.orderPrice,
            orderType: order.orderType,
            writerPrice: order.writerPrice,
            approveNeeded: order.approveNeeded,
            opti: order.opti,
            title: order.content?.title,
            writerNeeded: order.writerNeeded,
            reviewNeeded: order.reviewNeeded,
            publishedAt: order.publishedAt,
            draft: !order.draft,
        }));

        return { data: transformedData };
    },
};
